export interface CapacityProjectBreakdown {
  projectId: number;
  projectName?: string;
  allocatedHours: number;
  workedHours: number;
  estimatedHours: number;
}

export interface CapacityCell {
  weekStart: string;
  capacityHours: number;
  allocatedHours: number;
  workedHours: number;
  estimatedHours: number;
  utilizationPct: number;
  utilizationColor: "green" | "yellow" | "red";
  hasOverride: boolean;
  projects: CapacityProjectBreakdown[];
}

export interface CapacityTimelineRow {
  employeeId: number;
  employeeName?: string;
  jobDescription?: string;
  weeklyCapacityHours: number;
  cells: CapacityCell[];
}

export interface CapacityTimelineResponse {
  weeks: string[];
  rows: CapacityTimelineRow[];
}

// ── Visão diária (dia a dia) ──────────────────────────────────────────────────

export interface CapacityDailyCell {
  date: string;
  capacityHours: number;
  plannedHours: number;
  workedHours: number;
  isWeekend: boolean;
  isHoliday: boolean;
  hasOverride: boolean;
  utilizationPct: number;
  utilizationColor: "green" | "yellow" | "red";
}

export interface CapacityDailyRow {
  employeeId: number;
  employeeName?: string;
  jobDescription?: string;
  weeklyCapacityHours: number;
  cells: CapacityDailyCell[];
}

export interface CapacityDailyResponse {
  days: string[];
  rows: CapacityDailyRow[];
}

export interface CapacityEmployeeSummary {
  employeeId: number;
  employeeName?: string;
  capacityHours: number;
  allocatedHours: number;
  workedHours: number;
  estimatedHours: number;
  availableHours: number;
  utilizationPct: number;
  utilizationColor: "green" | "yellow" | "red";
}

export interface CapacityProjectSummary {
  projectId: number;
  projectName?: string;
  clientName?: string;
  allocatedHours: number;
  workedHours: number;
  estimatedHours: number;
}

export interface CapacityDashboardResponse {
  weekStart: string;
  teamCapacityHours: number;
  teamAllocatedHours: number;
  teamWorkedHours: number;
  teamEstimatedHours: number;
  teamAvailableHours: number;
  utilizationPct: number;
  overAllocatedCount: number;
  employees: CapacityEmployeeSummary[];
  projects: CapacityProjectSummary[];
}

export interface EmployeeCapacityHistoryItem {
  id: number;
  effectiveFrom: string; // yyyy-MM-dd
  weeklyCapacityHours: number;
}

export interface EmployeeLoadItem {
  employeeId: number;
  employeeName?: string;
  weekStart: string; // yyyy-MM-dd
  clientName?: string;
  projectName?: string;
  taskId: number;
  taskTitle: string;
  plannedHours: number;
}

export interface WeeklyAllocationViewModel {
  id: number;
  employeeId: number;
  employeeName?: string;
  projectId: number;
  projectName?: string;
  weekStart: string;
  allocatedHours: number;
  taskId?: number;
}

export enum UnavailabilityType {
  Vacation = 1,
  Sick = 2,
  Event = 3,
  Holiday = 4,
}

export const UNAVAILABILITY_LABELS: Record<number, string> = {
  [UnavailabilityType.Vacation]: "Férias",
  [UnavailabilityType.Sick]: "Atestado",
  [UnavailabilityType.Event]: "Evento",
  [UnavailabilityType.Holiday]: "Feriado",
};

export interface UnavailabilityViewModel {
  id: number;
  employeeId: number;
  employeeName?: string;
  type: number;
  typeName: string;
  startDate: string;
  endDate: string;
  hoursImpacted?: number;
  description?: string;
}

export interface CapacityOverrideViewModel {
  id: number;
  employeeId: number;
  employeeName?: string;
  weekStart: string;
  capacityHours: number;
  reason?: string;
}

export interface HolidayViewModel {
  id: number;
  date: string;
  description?: string;
}

export interface DayCapacityOverrideViewModel {
  id: number;
  employeeId: number;
  employeeName?: string;
  date: string;
  capacityHours: number;
  reason?: string;
}

export interface EstimateAllocationPreviewItem {
  employeeId: number;
  employeeName?: string;
  projectId: number;
  weekStart: string;
  estimatedHours: number;
  currentAllocatedHours: number;
}

export interface ApplyEstimatesResult {
  created: number;
  updated: number;
  skippedExisting: number;
}
